import React, { useState, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { Form, InputGroup, ListGroup, Spinner, Button } from "react-bootstrap";
import DataApi from "../../api/DataApi";
import { resolveEffectiveLookupFilterLogic } from "../../utils/lookupFilterUtils";

function isUuidString(s) {
  return /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(
    String(s || "").trim()
  );
}

/** Single form field value → string for lookup filter API (lookup id, multipicklist, boolean, etc.). */
function coerceFormFieldValueForLookupFilter(v) {
  if (v === null || v === undefined) return "";
  if (typeof v === "object" && v !== null && "id" in v) {
    const id = v.id;
    return id === null || id === undefined ? "" : String(id).trim();
  }
  if (Array.isArray(v)) {
    return v.map((x) => String(x).trim()).filter(Boolean).join(",");
  }
  if (typeof v === "boolean") return v ? "true" : "false";
  return String(v).trim();
}

const LOOKUP_FILTER_MERGE_RE = /\{\{\s*([a-zA-Z_][a-zA-Z0-9_.]*)\s*\}\}/g;
const LOOKUP_FILTER_ONLY_MERGE_RE = /^\{\{\s*([a-zA-Z_][a-zA-Z0-9_.]*)\s*\}\}$/;

function readFormValueAtPath(fieldValues, path) {
  if (!fieldValues || typeof fieldValues !== "object" || !path) return undefined;
  if (Object.prototype.hasOwnProperty.call(fieldValues, path)) {
    return fieldValues[path];
  }
  const parts = String(path).split(".").filter(Boolean);
  if (parts.length === 0) return undefined;

  const root = fieldValues[parts[0]];
  if (parts.length === 1) return root;
  // {{property_id.id}} → the selected lookup's id (form stores the uuid or {id,label})
  if (parts.length === 2 && parts[1].toLowerCase() === "id") return root;

  let current = root;
  for (let i = 1; i < parts.length; i++) {
    if (current == null) return current;
    if (typeof current !== "object") return undefined;
    const want = parts[i].toLowerCase();
    const key = Object.keys(current).find((k) => k.toLowerCase() === want);
    if (key == null) return undefined;
    current = current[key];
  }
  return current;
}

function guessObjectNameFromLookupField(fieldName) {
  const n = String(fieldName || "").trim();
  if (!n) return "";
  if (/_id$/i.test(n)) return n.replace(/_id$/i, "");
  return n;
}

function recordScalarFromFetchPayload(payload, fieldName) {
  if (!payload || typeof payload !== "object") return undefined;
  const flat = flattenViewRecordPayload(payload);
  const merged = { ...payload, ...flat };
  const want = String(fieldName || "").toLowerCase();
  const key = Object.keys(merged).find((k) => k.toLowerCase() === want);
  if (key == null) return undefined;
  return merged[key];
}

const parentLookupRecordCache = new Map();

async function fetchParentLookupFieldValue(lookupField, recordId, childField) {
  const objectName = guessObjectNameFromLookupField(lookupField);
  const id = String(recordId || "").trim();
  const child = String(childField || "").trim();
  if (!objectName || !id || !child) return undefined;
  const cacheKey = `${objectName}:${id}:${child}`;
  if (parentLookupRecordCache.has(cacheKey)) {
    return parentLookupRecordCache.get(cacheKey);
  }
  try {
    const dataApi = new DataApi(`object/view/${objectName}`);
    const res = await dataApi.fetchById(id);
    const payload = res?.data?.data ?? res?.data ?? {};
    const value = recordScalarFromFetchPayload(payload, child);
    if (value !== undefined) {
      parentLookupRecordCache.set(cacheKey, value);
    }
    return value;
  } catch (e) {
    console.warn("[LookupFilter] failed to resolve parent field", {
      lookupField,
      childField,
      error: e?.message || e,
    });
    return undefined;
  }
}

async function resolveMergePathValue(fieldValues, path) {
  let fv = readFormValueAtPath(fieldValues, path);
  const parts = String(path || "").split(".").filter(Boolean);
  const empty =
    fv === undefined || fv === null || (typeof fv !== "object" && String(fv).trim() === "");
  if (empty && parts.length === 2 && parts[1].toLowerCase() !== "id") {
    const parentId = coerceFormFieldValueForLookupFilter(
      readFormValueAtPath(fieldValues, parts[0])
    );
    if (parentId) {
      fv = await fetchParentLookupFieldValue(parts[0], parentId, parts[1]);
    }
    // Same-form field with the child API name (e.g. {{floor_id.property_id}} → property_id)
    if (fv === undefined || fv === null || fv === "") {
      const sameForm = readFormValueAtPath(fieldValues, parts[1]);
      if (sameForm !== undefined && sameForm !== null && sameForm !== "") {
        fv = sameForm;
      }
    }
  }
  return fv;
}

/**
 * Replace {{fieldName}} / {{lookup.id}} / {{lookup.child}} in filter values with
 * current form field values. Dotted parent fields (e.g. {{floor_id.property_id}})
 * load the selected parent record when the form only stores the lookup id.
 */
async function resolveLookupFilterTemplates(filters, fieldValues) {
  if (!filters || !Array.isArray(filters) || filters.length === 0) return filters;
  if (!fieldValues || typeof fieldValues !== "object") return filters;

  const resolved = [];
  for (const f of filters) {
    if (!f || typeof f !== "object") {
      resolved.push(f);
      continue;
    }
    const raw = f.value;
    if (raw === undefined || raw === null) {
      resolved.push({ ...f, value: null });
      continue;
    }

    const s = String(raw);
    const onlyPlaceholder = s.match(LOOKUP_FILTER_ONLY_MERGE_RE);
    if (onlyPlaceholder) {
      const fv = await resolveMergePathValue(fieldValues, onlyPlaceholder[1]);
      if (fv === null || fv === undefined) {
        resolved.push({ ...f, value: null, valueType: "field" });
      } else {
        resolved.push({
          ...f,
          value: coerceFormFieldValueForLookupFilter(fv),
          valueType: "field",
        });
      }
      continue;
    }

    const tokens = [...s.matchAll(LOOKUP_FILTER_MERGE_RE)];
    let out = s;
    for (const match of tokens) {
      const fv = await resolveMergePathValue(fieldValues, match[1]);
      out = out.replace(match[0], coerceFormFieldValueForLookupFilter(fv));
    }
    resolved.push({ ...f, value: out });
  }
  return resolved;
}

/** Flatten object/view API payload (sections + hidden fields) for label resolution. */
function flattenViewRecordPayload(data) {
  if (!data || typeof data !== "object") return {};
  const out = {};
  const take = (fields) => {
    if (!Array.isArray(fields)) return;
    fields.forEach((f) => {
      if (!f) return;
      const name = f.fieldName || f.field_name;
      if (!name) return;
      const v = f.value !== undefined && f.value !== null ? f.value : f.displayValue;
      if (v !== undefined && v !== null && String(v).trim() !== "") {
        out[name] = v;
      }
    });
  };
  if (Array.isArray(data.details)) {
    data.details.forEach((section) => take(section?.fields));
  }
  take(data.otherHiddenFields);
  return out;
}

/** User table column for active flag (not is_active). */
const ACTIVE_USER_LOOKUP_FILTER = {
  field: 'isactive',
  operator: 'equals',
  value: 'true',
};

/** True when field is a user lookup used for record ownership (ownerid, etc.). */
export function isOwnerUserLookupField(field) {
  if (!field) return false;
  const ref = String(
    field.referenceObject || field.reference_object || ''
  )
    .toLowerCase()
    .trim();
  if (ref !== 'user') return false;
  const fn = String(field.fieldName || field.field_name || '')
    .toLowerCase()
    .trim();
  if (fn === 'ownerid' || fn === 'owner_id') return true;
  if (fn.includes('owner')) return true;
  const label = String(field.label || field.field_label || '')
    .toLowerCase()
    .trim();
  return label.includes('owner');
}

function mergeActiveUserLookupFilters(filtersArray, objectName, activeUsersOnly) {
  if (!activeUsersOnly || String(objectName || '').toLowerCase() !== 'user') {
    return filtersArray;
  }
  const list = Array.isArray(filtersArray) ? [...filtersArray] : [];
  const hasActive = list.some(f => {
    const field = String(f?.field || '').toLowerCase();
    return field === 'isactive' || field === 'is_active';
  });
  if (!hasActive) {
    list.push(ACTIVE_USER_LOOKUP_FILTER);
  }
  return list;
}

function isInactiveUserLookupRow(item) {
  if (!item || typeof item !== 'object') return false;
  const raw = item.isactive ?? item.is_active;
  if (raw === undefined || raw === null) return false;
  if (raw === false || raw === 'false' || raw === 0 || raw === '0') return true;
  const disp =
    item.isactive_display != null
      ? String(item.isactive_display).trim().toLowerCase()
      : '';
  return disp === 'no' || disp === 'off' || disp === 'inactive';
}

const LookupLayout = ({
  objectName,
  searchFields,
  displayFields,
  value,
  defaultLabel,
  onChange,
  disabled,
  placeholder = "Search...",
  isRequired = false,
  lookupFilter = null, // Array of filter objects: [{field, operator, value}]
  lookupFilterLogic = 'AND', // AND or OR logic for combining filters
  /** When set, filter `value` strings may use {{fieldName}} placeholders resolved from this map (e.g. edit form state). */
  lookupFilterFieldValues = null,
  /** When lookup target is `recordview`, restrict rows to this CRM object (e.g. account on Account layout). */
  scopeRecordviewToObjectName = null,
  /** When set (non-empty), lookup suggestions for `recordview` are limited to these ids (role.permission_config). */
  recordviewIdAllowlist = null,
  /** Optional class on the root wrapper (e.g. align height with adjacent Form.Select). */
  rootClassName = "",
  /** z-index for portaled suggestion list (must exceed modal overlays, typically 10000+). */
  dropdownZIndex = 10100,
  /** When true and objectName is `user`, search only active users (for owner lookups). */
  activeUsersOnly = false,
}) => {


  const [searchTerm, setSearchTerm] = useState("");
  const [selected, setSelected] = useState(() => {
    if (!value || !defaultLabel) return null;
    const lt = String(defaultLabel).trim();
    const vt = String(value).trim();
    if (isUuidString(lt) && lt === vt) return null;
    return { id: value, label: defaultLabel };
  });
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0, width: 0 });
  const [isChangingSelection, setIsChangingSelection] = useState(false);
  const inputRef = useRef(null);
  const dropdownIdRef = useRef(`lookup-dropdown-${objectName}-${Date.now()}-${Math.random()}`);

  const searchKeys = searchFields?.split(",").map((f) => f.trim()) || [];
  const displayKeys = displayFields
    ? displayFields.split(",").map((f) => f.trim())
    : searchKeys;

  // Update selected state when value or defaultLabel props change
  useEffect(() => {
    if (isChangingSelection) return;
    if (value && defaultLabel) {
      const labelStr = String(defaultLabel).trim();
      const valueStr = String(value).trim();
      if (isUuidString(labelStr) && labelStr === valueStr) {
        setSelected(null);
      } else {
        setSelected({ id: value, label: defaultLabel });
      }
    } else if (!value) {
      setSelected(null);
    }
  }, [value, defaultLabel, isChangingSelection]);

  // When we have an id but no display label, load one record via view API (edit / hydration).
  useEffect(() => {
    const v = value && String(value).trim();
    if (!v || !objectName) return;
    const hasLabel = defaultLabel != null && String(defaultLabel).trim() !== "";
    if (hasLabel) return;

    let cancelled = false;
    (async () => {
      try {
        const dataApi = new DataApi(`object/view/${objectName}`);
        const res = await dataApi.fetchById(v);
        const payload = res?.data?.data;
        if (cancelled || !payload) return;
        const flat = flattenViewRecordPayload(payload);
        const label = displayKeys
          .map((key) => (flat[key] != null ? String(flat[key]).trim() : ""))
          .filter(Boolean)
          .join(" ");
        if (label) {
          setSelected({ id: v, label });
        }
      } catch {
        /* permission / missing record — leave unset */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [value, defaultLabel, objectName, displayFields]);

  // Calculate dropdown position when it should be shown
  useEffect(() => {
    if (showDropdown && suggestions.length > 0 && inputRef.current) {
      const updatePosition = () => {
        if (inputRef.current) {
          const rect = inputRef.current.getBoundingClientRect();
          setDropdownPosition({
            top: rect.bottom,
            left: rect.left,
            width: rect.width,
          });
        }
      };

      updatePosition();

      // Update position on scroll or resize
      window.addEventListener('scroll', updatePosition, true);
      window.addEventListener('resize', updatePosition);

      return () => {
        window.removeEventListener('scroll', updatePosition, true);
        window.removeEventListener('resize', updatePosition);
      };
    }
  }, [showDropdown, suggestions.length]);

  // Handle click outside to close dropdown
  useEffect(() => {
    if (!showDropdown) return;

    const handleClickOutside = (event) => {
      if (inputRef.current && !inputRef.current.contains(event.target)) {
        // Check if click is on the dropdown portal
        const dropdownElement = document.getElementById(dropdownIdRef.current);
        if (!dropdownElement || !dropdownElement.contains(event.target)) {
          setShowDropdown(false);
        }
      }
    };

    // Use a small delay to ensure the portal is rendered
    const timeoutId = setTimeout(() => {
      document.addEventListener('mousedown', handleClickOutside);
    }, 0);

    return () => {
      clearTimeout(timeoutId);
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showDropdown]);

  useEffect(() => {
    if (!searchTerm || searchTerm.trim().length < 2 || selected) {
      setSuggestions([]);
      return;
    }

    const fetchSuggestions = async () => {
      try {
        setLoading(true);
        const dataApi = new DataApi(`object/lookup/${objectName}`);

        // Build search params
        const searchParams = {
          q: searchTerm,
          fields: searchFields,
          display_fields: displayFields,
        };

        // Convert lookup_filter array to filters array format with logic (optional {{field}} placeholders)
        let filtersArray = [];
        const resolvedLookupFilter = await resolveLookupFilterTemplates(
          lookupFilter,
          lookupFilterFieldValues
        );
        if (
          resolvedLookupFilter &&
          Array.isArray(resolvedLookupFilter) &&
          resolvedLookupFilter.length > 0
        ) {
          // Include null and empty-string values so the API applies IS NULL / blank semantics.
          filtersArray = resolvedLookupFilter
            .filter(filter => filter.field && filter.operator && filter.value !== undefined)
            .map(filter => {
              // Handle operators that support multiple values (in, not_in)
              let filterValue = filter.value;
              if (filter.operator === 'in' || filter.operator === 'not_in') {
                if (filter.value == null) {
                  filterValue = filter.value;
                } else {
                  const values = String(filter.value)
                    .split(',')
                    .map(v => v.trim())
                    .filter(Boolean);
                  filterValue = values.length === 1 ? values[0] : values;
                }
              }
              return {
                field: filter.field,
                operator: filter.operator,
                value: filterValue,
                ...(filter.valueType ? { valueType: filter.valueType } : {}),
              };
            });
        }

        filtersArray = mergeActiveUserLookupFilters(
          filtersArray,
          objectName,
          activeUsersOnly
        );

        // Add filter for active roles only when objectName is 'role' (if no custom filters)
        if (filtersArray.length === 0 && objectName === 'role') {
          filtersArray = [{ field: 'is_active', operator: 'equals', value: 'true' }];
        }

        // Add filter for active user groups only when objectName is 'user_group' (if no custom filters)
        if (filtersArray.length === 0 && objectName === 'user_group') {
          filtersArray = [{ field: 'is_active', operator: 'equals', value: 'true' }];
        }

        let effectiveFilterLogic = resolveEffectiveLookupFilterLogic(lookupFilterLogic);

        const lookupTarget = String(objectName || '').toLowerCase().trim();
        const rvScope =
          scopeRecordviewToObjectName && String(scopeRecordviewToObjectName).trim();
        if (lookupTarget === 'recordview' && rvScope) {
          searchParams.recordviewForObject = rvScope;
        }

        // Add filters to search params if any exist
        if (filtersArray.length > 0) {
          searchParams.filters = JSON.stringify(filtersArray);
          searchParams.filterLogic = effectiveFilterLogic;
        }

        const res = await dataApi.search(searchParams);

        let rows = Array.isArray(res.data) ? res.data : [];
        const lookupTargetLc = String(objectName || '').toLowerCase().trim();
        const rvScopeEff =
          scopeRecordviewToObjectName && String(scopeRecordviewToObjectName).trim();
        if (
          lookupTargetLc === 'recordview' &&
          rvScopeEff &&
          Array.isArray(recordviewIdAllowlist) &&
          recordviewIdAllowlist.length > 0
        ) {
          const allow = new Set(recordviewIdAllowlist.map((x) => String(x)));
          rows = rows.filter((item) => allow.has(String(item.id)));
        }

        const dataWithLabels = rows.map((item) => ({
          ...item,
          label: displayKeys
            .map((key) => item[key]?.trim?.() || "")
            .filter(Boolean)
            .join(" "),
        }));

        setSuggestions(dataWithLabels);
      } catch (err) {
        console.error("Lookup error", err);
      } finally {
        setLoading(false);
        setShowDropdown(true);
      }
    };

    const delayDebounce = setTimeout(fetchSuggestions, 300);
    return () => clearTimeout(delayDebounce);
  }, [searchTerm, objectName, searchFields, displayFields, lookupFilter, lookupFilterLogic, lookupFilterFieldValues, scopeRecordviewToObjectName, recordviewIdAllowlist, activeUsersOnly]);

  const handleSelect = (item) => {
    const objLower = String(objectName || '').toLowerCase();
    if (
      (activeUsersOnly && objLower === 'user' && isInactiveUserLookupRow(item)) ||
      (objLower === 'user_group' && isInactiveUserLookupRow(item))
    ) {
      return;
    }
    setIsChangingSelection(false);
    setSelected(item);
    setSearchTerm("");
    setShowDropdown(false);
    onChange(item.id, item.label, item);
  };

  const handleClear = () => {
    setIsChangingSelection(false);
    setSelected(null);
    onChange(null, "", null);
  };

  const beginChangeSelection = () => {
    if (disabled) return;
    setIsChangingSelection(true);
    setSelected(null);
    setSearchTerm("");
    setShowDropdown(false);
  };

  return (
    <div
      style={{ position: "relative" }}
      ref={inputRef}
      className={rootClassName || undefined}
    >
      <InputGroup>
        {selected && !isChangingSelection ? (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              backgroundColor: "#f0f0f0",
              borderRadius: "4px",
              padding: "6px 12px",
              width: "100%",
              justifyContent: "space-between",
            }}
          >
            <span
              role="button"
              tabIndex={disabled ? -1 : 0}
              onClick={beginChangeSelection}
              onKeyDown={e => {
                if (!disabled && (e.key === 'Enter' || e.key === ' ')) {
                  e.preventDefault();
                  beginChangeSelection();
                }
              }}
              style={{
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
                flex: 1,
                cursor: disabled ? 'not-allowed' : 'pointer',
              }}
              title={disabled ? undefined : 'Click to change'}
            >
              {selected.label}
            </span>
            <Button
              variant="link"
              size="sm"
              onClick={handleClear}
              style={{
                color: "#000",
                textDecoration: "none",
                fontSize: "large",
                padding: "0px",
                margin: "0px",
              }}
              disabled={disabled}
            >
              ×
            </Button>
          </div>
        ) : (
          <>
            {value ? (
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  padding: "8px 12px",
                  border: "1px solid #ccc",
                  borderRadius: "6px",
                  minHeight: "40px",
                  backgroundColor: "#f8f9fa",
                  width: "100%",
                  justifyContent: "space-between",
                }}
              >
                <span style={{ flex: 1, fontWeight: 500 }}>{searchTerm}</span>

                <span
                  onClick={() => {
                    onChange(null, "", null);
                    setSearchTerm("");
                    setSuggestions([]);
                  }}
                  style={{
                    cursor: "pointer",
                    fontSize: "15px",
                    fontWeight: "bold",
                    color: "#dc3545",
                    padding: "0 6px",
                    lineHeight: 1,
                    userSelect: "none",
                  }}
                  title="Clear"
                >
                  &#10005; {/* Unicode for '×' cross */}
                </span>
              </div>
            ) : (
              <Form.Control
                type="text"
                value={searchTerm}
                placeholder={placeholder}
                disabled={disabled}
                onChange={(e) => setSearchTerm(e.target.value)}
                onFocus={() => {
                  if (searchTerm.length >= 2) setShowDropdown(true);
                }}
                onBlur={() => {
                  window.setTimeout(() => {
                    if (isChangingSelection && value && defaultLabel) {
                      setIsChangingSelection(false);
                    }
                  }, 200);
                }}
                required={isRequired}
              />
            )}

            {loading && (
              <InputGroup.Text>
                <Spinner animation="border" size="sm" />
              </InputGroup.Text>
            )}
          </>
        )}
      </InputGroup>

      {showDropdown && suggestions.length > 0 && !selected && typeof document !== 'undefined' && createPortal(
        <ListGroup
          id={dropdownIdRef.current}
          style={{
            position: "fixed",
            top: `${dropdownPosition.top}px`,
            left: `${dropdownPosition.left}px`,
            width: `${dropdownPosition.width}px`,
            zIndex: dropdownZIndex,
            maxHeight: 200,
            overflowY: "auto",
            background: "white",
            boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
            borderRadius: "4px",
            border: "1px solid #ccc",
            marginTop: "2px",
          }}
        >
          {suggestions.map((item) => (
            <ListGroup.Item
              action
              key={item.id}
              onClick={() => handleSelect(item)}
              style={{ cursor: "pointer" }}
            >
              {item.label}
            </ListGroup.Item>
          ))}
        </ListGroup>,
        document.body
      )}
    </div>
  );
};

export default LookupLayout;
